import React, { useEffect, useMemo, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { useAccount, useDisconnect } from "wagmi";
import HomeRpgLogoMark from "../home/rpg/HomeRpgLogoMark";
import { useAuth } from "../../context/AuthContext";
import { GATEWAY_NAV_TOP } from "../../data/dashboardMockData";
import { formatShortAddress, primaryLinkedWallet, shortAccountLabel } from "../../utils/walletDisplay";

const GATEWAY_DRAWER_BREAKPOINT_PX = 1024;
const GATEWAY_MOBILE_NAV_MQ = `(max-width: ${GATEWAY_DRAWER_BREAKPOINT_PX}px)`;

function useGatewayMobileNav() {
  const [mobile, setMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia(GATEWAY_MOBILE_NAV_MQ).matches;
  });

  useEffect(() => {
    const mq = window.matchMedia(GATEWAY_MOBILE_NAV_MQ);
    const sync = () => setMobile(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  return mobile;
}

function gatewayLinkClass(active, extra = "") {
  return `dg-topnav__link${active ? " is-active" : ""}${extra ? ` ${extra}` : ""}`;
}

const GatewayTopNav = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const { disconnect } = useDisconnect();
  const { address } = useAccount();
  const isMobileNav = useGatewayMobileNav();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const closeDrawer = () => setDrawerOpen(false);

  const linked = useMemo(() => primaryLinkedWallet(user), [user]);
  const shortWallet = linked?.address
    ? formatShortAddress(linked.address)
    : address
      ? formatShortAddress(address)
      : "";
  const label = useMemo(() => shortAccountLabel(user), [user]);

  useEffect(() => {
    setDrawerOpen(false);
  }, [isMobileNav]);

  useEffect(() => {
    if (!drawerOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setDrawerOpen(false);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [drawerOpen]);

  useEffect(() => {
    if (!isMobileNav || !drawerOpen) {
      document.body.classList.remove("dg-drawer-open");
      return undefined;
    }
    document.body.classList.add("dg-drawer-open");
    return () => document.body.classList.remove("dg-drawer-open");
  }, [drawerOpen, isMobileNav]);

  const onLogout = () => {
    closeDrawer();
    logout();
    try {
      disconnect?.();
    } catch {
      /* noop */
    }
  };

  const renderNavItem = (item, onNavigate) => {
    if (item.stub) {
      return (
        <button
          key={item.label}
          type="button"
          className={gatewayLinkClass(false, "dg-topnav__link--ghost")}
          title="Coming soon in Gateway"
          onClick={onNavigate}
        >
          {item.label}
        </button>
      );
    }

    return (
      <NavLink
        key={item.label}
        to={item.path}
        end={Boolean(item.end)}
        onClick={onNavigate}
        className={({ isActive }) => gatewayLinkClass(isActive)}
      >
        {item.label}
      </NavLink>
    );
  };

  const renderIdentity = (layout) => {
    if (!isAuthenticated) return null;

    return (
      <div className={`dg-topnav__identity${layout === "drawer" ? " dg-topnav__identity--drawer" : ""}`}>
        {shortWallet ? (
          <span className="dg-topnav__chip dg-topnav__chip--wallet" title={linked?.address || address}>
            {shortWallet}
          </span>
        ) : null}
        {label ? (
          <span className="dg-topnav__chip dg-topnav__chip--user" title={user?.email || user?.username}>
            {label}
          </span>
        ) : null}
        <NavLink
          to="/dashboard"
          end
          className={({ isActive }) => `dg-topnav__btn dg-topnav__btn--dash${isActive ? " is-active" : ""}`}
          onClick={closeDrawer}
        >
          Dashboard
        </NavLink>
        <button type="button" className="dg-topnav__btn dg-topnav__btn--logout" onClick={onLogout}>
          Logout
        </button>
      </div>
    );
  };

  return (
    <header
      className={`dg-topnav${drawerOpen && isMobileNav ? " dg-topnav--drawer-open" : ""}`}
      role="banner"
    >
      <div className="dg-topnav__bar">
        <div className="dg-topnav__inner">
          {isMobileNav ? (
            <button
              type="button"
              className="dg-topnav__burger"
              aria-expanded={drawerOpen}
              aria-controls="dg-gateway-drawer"
              aria-label={drawerOpen ? "Close menu" : "Open menu"}
              onClick={() => setDrawerOpen((open) => !open)}
            >
              <i className={`fas ${drawerOpen ? "fa-times" : "fa-bars"}`} aria-hidden />
              <span className="dg-topnav__burger-label">Menu</span>
            </button>
          ) : null}

          <Link to="/" className="dg-topnav__logo" aria-label="Treydora home" onClick={closeDrawer}>
            <HomeRpgLogoMark />
          </Link>

          {!isMobileNav ? (
            <nav className="dg-topnav__center" aria-label="Gateway">
              {GATEWAY_NAV_TOP.map((item) => renderNavItem(item, closeDrawer))}
            </nav>
          ) : null}

          {!isMobileNav ? (
            <div className="dg-topnav__right dg-topnav__cluster">
              <button
                type="button"
                className="dg-icon-btn"
                disabled
                aria-label="Notifications (coming soon)"
                title="Notifications — coming soon"
              >
                <i className="fas fa-bell" />
                <span className="dg-icon-dot" aria-hidden />
              </button>
              <button
                type="button"
                className="dg-icon-btn"
                disabled
                aria-label="Messages (coming soon)"
                title="Messages — coming soon"
              >
                <i className="fas fa-comment-dots" />
              </button>
              {renderIdentity("desktop")}
            </div>
          ) : null}
        </div>
      </div>

      {isMobileNav ? (
        <>
          <button
            type="button"
            className={`dg-drawer-backdrop${drawerOpen ? " is-visible" : ""}`}
            aria-label="Close menu"
            aria-hidden={!drawerOpen}
            tabIndex={drawerOpen ? 0 : -1}
            onClick={closeDrawer}
          />
          <div
            className={`dg-drawer${drawerOpen ? " is-open" : ""}`}
            id="dg-gateway-drawer"
            aria-hidden={!drawerOpen}
          >
            <nav className="dg-drawer__nav" aria-label="Gateway">
              {GATEWAY_NAV_TOP.map((item) => renderNavItem(item, closeDrawer))}
            </nav>
            <div className="dg-drawer__tools">
              <button
                type="button"
                className="dg-icon-btn"
                disabled
                aria-label="Notifications (coming soon)"
                title="Notifications — coming soon"
              >
                <i className="fas fa-bell" />
              </button>
              <button
                type="button"
                className="dg-icon-btn"
                disabled
                aria-label="Messages (coming soon)"
                title="Messages — coming soon"
              >
                <i className="fas fa-comment-dots" />
              </button>
            </div>
            {renderIdentity("drawer")}
          </div>
        </>
      ) : null}
    </header>
  );
};

export default GatewayTopNav;
